// A mistyped mail provider in the request form's address (src/main.ts): the
// letter with the workspace link goes nowhere, and the person never learns why.
// Pure, so it is tested alone.

/** The providers the site's visitors write from - the inboxes `sent.ts` opens,
 *  and the big international ones. A domain on this list is never "corrected". */
const PROVIDERS = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'icloud.com',
  'yahoo.com',
  'proton.me',
  'protonmail.com',
  'ukr.net',
  'i.ua',
  'meta.ua',
  'wp.pl',
  'o2.pl',
  'onet.pl',
  'op.pl',
  'interia.pl',
  'gmx.com',
  'gmx.net',
  'gmx.de',
];

/** Real domains a near-miss rule would otherwise "fix" into a provider (mail.com is not gmail.com). */
const ALSO_REAL = new Set([
  'mail.com', 'me.com', 'mac.com', 'msn.com', 'aol.com', 'onet.eu', 'interia.eu', 'ua.fm', 'email.ua', 'bigmir.net',
  // The big providers' own country domains: two letters from the .com, and real.
  'protonmail.ch', 'proton.me', 'pm.me', 'gmx.at', 'gmx.ch', 'gmx.de', 'yahoo.ca', 'yahoo.de', 'yahoo.fr', 'yahoo.it',
  'yahoo.es', 'yahoo.co.uk', 'outlook.cz', 'outlook.de', 'outlook.fr', 'outlook.it', 'outlook.es', 'hotmail.de',
  'hotmail.fr', 'hotmail.it', 'hotmail.es', 'hotmail.co.uk', 'live.de', 'live.fr', 'live.co.uk',
]);

/** Edits (insert, delete, substitute, swap two neighbours) between a and b. */
function distance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array<number>(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0]![j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, d[i - 2]![j - 2]! + 1);
      d[i]![j] = v;
    }
  }
  return d[a.length]![b.length]!;
}

/**
 * The address with its provider's domain spelled right - `anna@gmail.con` →
 * `anna@gmail.com`, `anna@ukr.ner` → `anna@ukr.net`, `anna@gmail` →
 * `anna@gmail.com` - or null when the domain is a provider's, a real one of its
 * own, or not close enough to any to guess. Close is one edit for a short
 * domain and two for a longer one: past that, a guess corrects more right
 * addresses than wrong ones.
 */
export function suggestEmail(raw: string): string | null {
  const email = raw.trim();
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1) return null;
  const domain = email.slice(at + 1).toLowerCase();
  if (PROVIDERS.includes(domain) || ALSO_REAL.has(domain)) return null;
  let best: { domain: string; d: number } | null = null;
  for (const p of PROVIDERS) {
    // A provider's name with the ending left off: gmail → gmail.com.
    const d = p.startsWith(`${domain}.`) && !domain.includes('.') ? 1 : distance(domain, p);
    if (d < (best?.d ?? Infinity)) best = { domain: p, d };
  }
  if (!best) return null;
  const allowed = best.domain.length <= 6 ? 1 : 2;
  return best.d > 0 && best.d <= allowed ? `${email.slice(0, at)}@${best.domain}` : null;
}
